import {Component, inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {ProductService} from '../../../../services/product.service';
import {
  OrderOfferProductsDetailsDialogComponent
} from '../../order-offer-products-dialog/order-offer-products-details-dialog/order-offer-products-details-dialog.component';
import {CartService} from '../../../../services/cart.service';
import {NotificationService} from '../../../../notification/notification.service';
import {NotificationType} from '../../../../enum/notification-type.enum';
import {THEME_CONFIG} from '../../../../../environments/tokens/theme.token';
import {CdkScrollable} from '@angular/cdk/scrolling';
import {MatFormField} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatButton} from '@angular/material/button';
import {DecimalPipe, NgOptimizedImage} from '@angular/common';

interface SelectedItem {
  product: any;
  size?: any;
  configuration?: any;
  quantity: number;
  totalPrice?: number;
}

interface ProductSelection {
  quantity: number;
  selectedSizeId: string | null;
}

@Component({
  selector: 'app-product-discount-dialog',
  templateUrl: './product-discount-dialog.component.html',
  styleUrl: './product-discount-dialog.component.scss',
  standalone: true,
  imports: [CdkScrollable, MatDialogContent, MatFormField, MatSelect,
    MatOption, MatButton, DecimalPipe, NgOptimizedImage]
})
export class ProductDiscountDialogComponent implements OnInit {
  readonly data = inject<{ promotion: any; rule: any }>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<ProductDiscountDialogComponent>);
  private productService = inject(ProductService);
  private cartService = inject(CartService);
  private notificationService = inject(NotificationService);
  selectedItems: SelectedItem[] = [];
  products: any[] = [];
  private dialog = inject(MatDialog);
  selections = new Map<string, ProductSelection>();
  private productCategoryMap = new Map<string, any>();
  private productCache = new Map<string, any>();
  protected readonly themeConfig = inject(THEME_CONFIG)

  ngOnInit(): void {
    const categories: any[] = this.data.rule?.categories || [];
    this.products = categories.flatMap((cat: any) => {
      const prods = (cat.products || []).filter((p: any) => p.available !== false);
      prods.forEach((p: any) => this.productCategoryMap.set(p.id, cat));
      return prods;
    });
    this.products.forEach(p =>
      this.selections.set(p.id, {quantity: 0, selectedSizeId: null})
    );
    console.log('products', this.products)
  }

  productHasSizes(product: any): boolean {
    return typeof product.price === 'object' && product.price !== null;
  }

  isSetPriceReduction() {
    return this.data.rule?.reduction?.type === 'SET_PRICE';
  }

  getProductSizes(product: any): { id: string; name: string }[] {
    const cat = this.productCategoryMap.get(product.id);
    return cat?.sizes || [];
  }

  parsePrice(priceStr: string): number {
    if (!priceStr) return 0;
    return parseFloat(priceStr.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
  }

  getProductPrice(product: any, sizeId?: string | null): number {
    const price = product.price;

    if (typeof price === 'string') {
      return this.parsePrice(price);
    }

    if (typeof price === 'object' && price !== null) {
      if (!sizeId) return 0;
      const sizeName = this.getProductSizes(product).find(s => s.id === sizeId)?.name;
      if (sizeName && price[sizeName]) {
        return this.parsePrice(price[sizeName]);
      }
    }
    return 0;
  }

  getDisplayPrice(product: any): number {
    const sel = this.selections.get(product.id);
    if (this.productHasSizes(product)) {
      return this.getProductPrice(product, sel?.selectedSizeId || null);
    }
    return this.getProductPrice(product, sel?.selectedSizeId);
  }

  sel(productId: string): ProductSelection {
    return this.selections.get(productId) ?? {quantity: 0, selectedSizeId: null};
  }

  getDiscountedUnitPrice(product: any): number {
    const basePrice = this.getDisplayPrice(product);
    if (basePrice <= 0) return 0;

    const reduction = this.data.rule?.reduction;
    if (!reduction) return basePrice;

    if (reduction.type === 'PERCENTAGE') {
      return Math.round(basePrice * (1 - (reduction.value || 0)) * 100) / 100;
    }

    if (reduction.type === 'FIXED_AMOUNT') {
      return Math.max(0, Math.round((basePrice - (reduction.value || 0)) * 100) / 100);
    }

    if (reduction.type === 'SET_PRICE') {
      return reduction.value || 0;
    }

    return basePrice;
  }

  onSizeChange(product: any, sizeId: string | null): void {
    const sel = this.sel(product.id);
    this.selections.set(product.id, {
      ...sel,
      selectedSizeId: sizeId || null
    });
  }

  canIncrement(product: any): boolean {
    if (this.productHasSizes(product)) {
      return !!this.sel(product.id).selectedSizeId;
    }
    return true;
  }

  hasProductOptions(product: any): boolean {
    return (
      (product.ingredients && product.ingredients.length > 0) ||
      (product.supplements && product.supplements.length > 0) ||
      (product.additions && product.additions.length > 0)
    );
  }

  increment(product: any): void {
    if (!this.canIncrement(product)) return;

    // ✅ Si déjà en cache → pas d'appel API
    if (this.productCache.has(product.id)) {
      this.handleIncrementLogic(product, this.productCache.get(product.id));
      return;
    }

    this.productService.getProductById(product.id).subscribe({
      next: (response: any) => {
        const fullProduct = response?.body?.data || product;
        // ✅ Stocker en cache pour les prochains clics
        this.productCache.set(product.id, fullProduct);
        this.handleIncrementLogic(product, fullProduct);
      },
      error: (err) => {
        console.error('Erreur produit', err);
      }
    });
  }

// ✅ Logique extraite — appelée depuis le cache ou depuis l'API
  private handleIncrementLogic(product: any, fullProduct: any): void {
    const hasOptions =
      (fullProduct.additions && fullProduct.additions.length > 0) ||
      (fullProduct.ingredients && fullProduct.ingredients.length > 0) ||
      (fullProduct.supplements && fullProduct.supplements.length > 0);

    if (!hasOptions) {
      const sel = this.sel(product.id);
      this.selections.set(product.id, {
        ...sel,
        quantity: sel.quantity + 1
      });
      return;
    }
    const currentSelection = this.sel(product.id);
    const selectedSizeName = this.getProductSizes(product)
      .find(s => s.id === currentSelection.selectedSizeId)?.name || null;
    const detailDialogRef = this.dialog.open(OrderOfferProductsDetailsDialogComponent, {
      panelClass: 'custom-dialog',
      width: '500px',
      disableClose: true,
      data: {
        product: fullProduct,
        rule: {
          ...this.data.rule,
          selectedSizeId: currentSelection.selectedSizeId,
          selectedSizeName: selectedSizeName

        },
        isDiscountMode: true
      }
    });

    detailDialogRef.afterClosed().subscribe((result: any) => {
      if (result?.action === 'confirm' && result.configuration) {

        // ✅ FIX NG0100 : On isole la mise à jour de l'état dans un macro-task (setTimeout)
        setTimeout(() => {
          console.log('configuration reçue:', result.configuration);
          const sizeId = result.size?.id || this.sel(product.id).selectedSizeId || null;
          const sizeObj = sizeId
            ? this.getProductSizes(product).find(s => s.id === sizeId) || null
            : null;

          const selectedSizeName = this.getProductSizes(product)
            .find(s => s.id === sizeId)?.name || null;

          const supplementsTotal = (result.configuration.selectedSupplements || []).reduce(
            (sum: number, sup: any) => sum + (this.parseSupPrice(sup.price || 0) * (sup.quantity || 1)),
            0
          );

          const basePrice = this.getProductPrice(product, sizeId);
          const itemTotalPrice = basePrice + supplementsTotal;

          const newItem: SelectedItem = {
            product: fullProduct,
            size: sizeObj,
            configuration: {
              ...result.configuration,
              selectedSizeName
            },
            quantity: result.quantity || 1,
            totalPrice: Math.round(itemTotalPrice * 100) / 100
          };

          // Mutation propre du tableau pour notifier le template
          this.selectedItems = [...this.selectedItems, newItem];

          const currentSel = this.sel(product.id);
          this.selections.set(product.id, {
            ...currentSel,
            quantity: currentSel.quantity + (result.quantity || 1),
            selectedSizeId: sizeId
          });
        });
      }
    });
  }

  getAllSelectedSupplements(): any[] {
    const allSupps: any[] = [];

    this.selectedItems.forEach((item, itemIdx) => {
      const supps = item.configuration?.selectedSupplements || [];
      supps.forEach((sup: any, supIdx: number) => {
        const price = this.parseSupPrice(sup.price);
        allSupps.push({
          // ✅ Clé unique combinant l'ID du produit, l'index de l'item et du supplément
          uniqueId: `${item.product.id}_${itemIdx}_${sup.id || supIdx}`,
          name: sup.name || sup.title || 'Supplément',
          price: price,
          quantity: sup.quantity || 1
        });
      });
    });

    return allSupps;
  }

  parseSupPrice(price: any): number {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      return parseFloat(price.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
    }
    return 0;
  }

  decrement(product: any): void {
    const sel = this.sel(product.id);
    if (sel.quantity <= 0) return;

    // ✅ Remove the last selectedItem for this product (removes its supplements)
    const lastIndex = this.selectedItems.map(i => i.product.id).lastIndexOf(product.id);
    if (lastIndex !== -1) {
      this.selectedItems.splice(lastIndex, 1);
    }

    this.selections.set(product.id, {...sel, quantity: sel.quantity - 1});
  }

  getDiscountAmount(): number {
    const reduction = this.data.rule?.reduction;
    if (!reduction) return 0;

    if (reduction.type === 'PERCENTAGE') {
      const basePrice = this.getTotalBasePrice();
      return Math.round(basePrice * (reduction.value || 0) * 100) / 100;
    }

    if (reduction.type === 'FIXED_AMOUNT') {
      // Calculer le nombre total de produits sélectionnés toutes catégories confondues
      const totalQuantity = this.products.reduce((sum, p) => sum + (this.sel(p.id).quantity || 0), 0);

      // La remise totale est égale à : (Valeur de la remise) × (Quantité totale de produits)
      return (reduction.value || 0) * totalQuantity;
    }
    return 0;
  }

  getSelectedSupplementsForProduct(productId: string): any[] {
    return this.selectedItems
      .filter(item => item.product.id === productId)
      .flatMap(item =>
        (item.configuration?.selectedSupplements || []).map((sup: any) => ({
          ...sup,
          price: this.parseSupPrice(sup.price)
        }))
      );
  }

  getTotalBasePrice(): number {
    let totalBase = 0;   // ← Seulement le prix des produits (sans suppléments)

    this.products.forEach(p => {
      const sel = this.sel(p.id);
      if (sel.quantity > 0) {
        totalBase += this.getProductPrice(p, sel.selectedSizeId) * sel.quantity;
      }
    });

    return totalBase;
  }

  getTotalSupplements(): number {
    let totalSupps = 0;

    this.selectedItems.forEach(item => {
      const supps = item.configuration?.selectedSupplements || [];
      supps.forEach((sup: any) => {
        const price = this.parseSupPrice(sup.price);
        totalSupps += price * (sup.quantity || 1);
      });
    });

    return totalSupps;
  }

  // getTotalBasePrice(): number {
  //   let total = 0;
  //   this.products.forEach(p => {
  //     const sel = this.sel(p.id);
  //     if (sel.quantity > 0) {
  //       total += this.getProductPrice(p, sel.selectedSizeId) * sel.quantity;
  //     }
  //   });
  //   return total;
  // }

  getTotalAfterDiscount(): number {
    const basePrice = this.getTotalBasePrice();           // prix des produits seulement
    const supplements = this.getTotalSupplements();       // suppléments plein tarif

    const reduction = this.data.rule?.reduction;

    let discountedBase = basePrice;
    if (reduction?.type === 'PERCENTAGE') {
      discountedBase = Math.max(0, basePrice * (1 - (reduction.value || 0)));
    } else if (reduction?.type === 'FIXED_AMOUNT') {
      // Calculer la quantité totale de tous les produits sélectionnés
      const totalQty = this.products.reduce((sum, p) => sum + (this.sel(p.id).quantity || 0), 0);

      // Soustraire (Valeur de la remise unitaire * Quantité totale) du prix de base
      discountedBase = Math.max(0, basePrice - ((reduction.value || 0) * totalQty));
    } else if (reduction?.type === 'SET_PRICE') {
      const totalQty = this.products.reduce((sum, p) => sum + (this.sel(p.id).quantity || 0), 0);
      discountedBase = (reduction.value || 0) * totalQty;
    }

    // Total final = base après remise + suppléments (non remisés)
    return Math.round((discountedBase + supplements) * 100) / 100;
  }

  hasSelection(): boolean {
    return this.products.some(p => (this.sel(p.id).quantity ?? 0) > 0);
  }

  // confirm(): void {
  //   const reduction = this.data.rule?.reduction || null;
  //   const activatedFor: string[] = this.data.promotion?.payload?.activatedFor || [];
  //   const orderTypes: string[] = this.data.promotion?.payload?.orderTypes || [];
  //   const promotionId: string = this.data.promotion?.id || null;
  //   const promoCode: string = this.data.promotion?.promoCode || null; // ✅ AJOUTER
  //
  //   this.products.forEach(product => {
  //     const sel = this.sel(product.id);
  //     if (sel.quantity <= 0) return;
  //
  //     const basePrice = this.getProductPrice(product, sel.selectedSizeId);
  //     let discountedPrice = basePrice;
  //
  //     if (reduction?.type === 'PERCENTAGE') {
  //       discountedPrice = Math.round(basePrice * (1 - (reduction.value || 0)) * 100) / 100;
  //     } else if (reduction?.type === 'FIXED_AMOUNT') {
  //       discountedPrice = Math.max(0, Math.round((basePrice - (reduction.value || 0)) * 100) / 100);
  //     }
  //
  //     // ✅ Chercher item existant dans le panier
  //     const cartItem = this.cartService.items().find(
  //       item =>
  //         item.type === CartItemType.PRODUCT &&
  //         (item as ProductCartItem).product.id === product.id &&
  //         (sel.selectedSizeId === null ||
  //           (item as ProductCartItem).selectedSize === sel.selectedSizeId)
  //     ) as ProductCartItem | undefined;
  //
  //     if (!cartItem) {
  //       const total = Math.round(discountedPrice * sel.quantity * 100) / 100;
  //       this.cartService.addProduct({
  //         product: {...product, price: basePrice},
  //         productImage: product.image || '',
  //         selectedSize: sel.selectedSizeId,
  //         selectedPrice: discountedPrice,
  //         quantity: sel.quantity,
  //         selectedIngredients: product.ingredients?.map((i: any) => i.id) || [],
  //         excludedIngredients: [],
  //         selectedAdditions: {},
  //         selectedSupplements: {},
  //         total: total,
  //         discountActivatedFor: activatedFor,
  //         discountOrderTypes: orderTypes,
  //         discountPromotionId: promotionId,
  //         reduction: reduction,
  //         addedByPromoCode: true,
  //         discountPromoCode: promoCode // ✅
  //       });
  //       return;
  //     }
  //
  //     // ✅ Item existant → appliquer remise
  //     const supplementsTotal = Object.keys(cartItem.selectedSupplements).reduce((sum, suppId) => {
  //       const supplement = cartItem.product?.supplements?.find((s: any) => s.id === suppId);
  //       if (!supplement) return sum;
  //       let price = supplement.price || 0;
  //       if (cartItem.selectedSize && supplement.pricePerSizes?.length > 0) {
  //         const priced = supplement.pricePerSizes.find((ps: any) => ps.size.id === cartItem.selectedSize);
  //         if (priced?.price) price = priced.price;
  //       }
  //       return Math.round((sum + price * (cartItem.selectedSupplements[suppId] || 1)) * 100) / 100;
  //     }, 0);
  //
  //     const newTotal = Math.round((discountedPrice + supplementsTotal) * cartItem.quantity * 100) / 100;
  //
  //     this.cartService.applyDiscountToExistingItem(cartItem.id, {
  //       selectedPrice: discountedPrice,
  //       discountPromotionId: promotionId,
  //       discountActivatedFor: activatedFor,
  //       discountOrderTypes: orderTypes,
  //       reduction: reduction,
  //       total: newTotal,
  //       addedByPromoCode: true,
  //       discountPromoCode: promoCode // ✅
  //     });
  //   });
  //
  //   this.notificationService.sendNotification(NotificationType.SUCCESS, 'Remise appliquée avec succès !', '');
  //   this.dialogRef.close({action: 'confirm'});
  // }
  confirm(): void {
    console.log('data.promotion:', this.data.promotion);
    console.log('data.rule:', this.data.rule);
    const reduction = this.data.rule?.reduction || null;
    const activatedFor: string[] = this.data.promotion?.payload?.activatedFor || [];
    const orderTypes: string[] = this.data.promotion?.payload?.orderTypes || [];
    const promotionId: string = this.data.promotion?.id || null;
    const promoCode: string = this.data.promotion?.promoCode || null;
    console.log('promotionId:', promotionId);
    console.log('reduction:', reduction);
    this.products.forEach(product => {
      const sel = this.sel(product.id);
      if (sel.quantity <= 0) return;

      const basePrice = this.getProductPrice(product, sel.selectedSizeId);
      let discountedPrice = basePrice;
      if (reduction?.type === 'PERCENTAGE') {
        discountedPrice = Math.round(basePrice * (1 - (reduction.value || 0)) * 100) / 100;
      } else if (reduction?.type === 'FIXED_AMOUNT' || reduction?.type === 'SET_PRICE') {
        discountedPrice = Math.max(0, Math.round((basePrice - (reduction.value || 0)) * 100) / 100);
      }
      const productWithNumericPrice = {
        ...product,
        price: basePrice
      };

      const configuredItems = this.selectedItems.filter(
        item => item.product?.id === product.id
      );

      if (configuredItems.length > 0) {
        configuredItems.forEach(configItem => {
          const config = configItem.configuration;
          const fullProd = configItem.product;
          const itemSizeId = configItem.size?.id || sel.selectedSizeId || null;
          const itemBasePrice = this.getProductPrice(product, itemSizeId);

          let itemDiscountedPrice = itemBasePrice;
          if (reduction?.type === 'PERCENTAGE') {
            itemDiscountedPrice = Math.round(itemBasePrice * (1 - (reduction.value || 0)) * 100) / 100;
          } else if (reduction?.type === 'FIXED_AMOUNT' || reduction?.type === 'SET_PRICE') {
            itemDiscountedPrice = Math.max(0, Math.round((itemBasePrice - (reduction.value || 0)) * 100) / 100);
          }
          //
          // const selectedSupplementsObj: { [suppId: string]: number } = {};
          // (config?.selectedSupplements || []).forEach((sup: any) => {
          //   selectedSupplementsObj[sup.id] = sup.quantity || 1;
          // });
          //
          // const supplementsTotal = (config?.selectedSupplements || []).reduce((sum: number, sup: any) => {
          //   return Math.floor((sum + (sup.price || 0) * (sup.quantity || 1)) * 100) / 100;
          // }, 0);
          const selectedSupplementsObj: { [suppId: string]: number } = {};
          let supplementsTotal = 0;

          (config?.selectedSupplements || []).forEach((sup: any) => {
            const suppPrice = this.parseSupPrice(sup.price) || 0;   // Utilise le prix qu'on a forcé
            selectedSupplementsObj[sup.id] = sup.quantity || 1;
            supplementsTotal += suppPrice * (sup.quantity || 1);
          });
          supplementsTotal = Math.round(supplementsTotal * 100) / 100;
          const selectedAdditionsObj: { [additionId: string]: { [itemId: string]: number } } = {};
          (config?.selectedAccompaniments || []).forEach((acc: any) => {
            if (!selectedAdditionsObj[acc.additionId]) {
              selectedAdditionsObj[acc.additionId] = {};
            }
            selectedAdditionsObj[acc.additionId][acc.id] = acc.quantity || 1;
          });

          const itemTotal = Math.round((itemDiscountedPrice + supplementsTotal) * configItem.quantity * 100) / 100;

          const productWithNumericPrice = {
            ...fullProd,
            price: itemBasePrice,
            supplements: fullProd.supplements || [],
            additions: fullProd.additions || [],
            ingredients: fullProd.ingredients || []
          };

          this.cartService.addProduct({
            product: productWithNumericPrice,
            productImage: fullProd.image || '',
            selectedSize: itemSizeId,
            selectedPrice: itemDiscountedPrice,
            quantity: configItem.quantity,
            selectedIngredients: fullProd.ingredients?.map((i: any) => i.id) || [],
            excludedIngredients: config?.removedIngredients?.map((i: any) => i.id) || [],
            selectedAdditions: selectedAdditionsObj,
            selectedSupplements: selectedSupplementsObj,
            total: itemTotal,
            discountActivatedFor: activatedFor,
            discountOrderTypes: orderTypes,
            discountPromotionId: promotionId,
            reduction: reduction,
            addedByPromoCode: true,
            discountPromoCode: promoCode
          });
        });
      } else {
        const total = Math.round(discountedPrice * sel.quantity * 100) / 100;

        this.cartService.addProduct({
          product: {...productWithNumericPrice},
          productImage: product.image || '',
          selectedSize: sel.selectedSizeId,
          selectedPrice: discountedPrice,
          quantity: sel.quantity,
          selectedIngredients: product.ingredients?.map((i: any) => i.id) || [],
          excludedIngredients: [],
          selectedAdditions: {},
          selectedSupplements: {},
          total: total,
          discountActivatedFor: activatedFor,
          discountOrderTypes: orderTypes,
          discountPromotionId: promotionId,
          reduction: reduction,
          addedByPromoCode: true,
          discountPromoCode: promoCode

        });
      }
    });

    this.notificationService.sendNotification(
      NotificationType.SUCCESS,
      'Produits ajoutés au panier avec succès !',
      ''
    );
    this.dialogRef.close({action: 'confirm'});
  }

  cancel(): void {
    this.dialogRef.close({action: 'cancel'});
  }
}
